import * as React from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import {
    addLieferantenrechnungAraPosition,
    LieferantenrechnungAraPosition,
    defaultLieferantenrechnungAraPosition,
    updateLieferantenrechnungAraPosition,
    useLieferantenrechnungAraPositionList,
} from 'api/lieferantenrechnungAraPosition';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { cloneDeep, isEqual } from 'lodash';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { Timestamps } from 'formComponents/Timestamps';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { ArtikelDetailKalkulation, getArtikelDetailKalkulationen } from 'api/artikelDetailKalkulation';
import { Kunde } from 'api/kunde';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { logDifference } from 'utils/helper';
import { ArtikelDetailSelect } from '../Lager/ArtikelDetail/ArtikelDetailSelect';

const styles = mergeStyleSets({
    artikelLine: {
        marginTop: 5,
        display: 'grid',
        // gridTemplateColumns: '80px 1fr 3fr',
        gridTemplateColumns: '80px 1fr',
        gridColumnGap: 10,
    },
    textLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
    addLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '6fr 1fr',
        gridColumnGap: 10,
    },
    posLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gridColumnGap: 10,
    },
    lastLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: 10,
    },
    varianteHeader: {
        marginTop: 15,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 700,
        backgroundColor: '#EEEEEE',
        paddingTop: 10,
    },
    variante: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: 10,
        backgroundColor: '#EEEEEE',
        padding: '0 10px 10px 10px',
    },
});

interface LieferantenrechnungAraPositionEditProps {
    kunde: Kunde;
    rNr: string;
    rPos: number;
    pos: number; // -1 = new
    readOnly?: boolean;
    onClose: (refetch: boolean) => void;
}

export const LieferantenrechnungAraPositionEdit: React.FC<LieferantenrechnungAraPositionEditProps> = ({
    kunde,
    rNr,
    rPos,
    pos,
    readOnly = false,
    onClose,
}) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const lieferantenrechnungAraPositionList = useLieferantenrechnungAraPositionList(
        {
            rNr,
            rPos,
            pos,
        },
        { enabled: pos !== -1 },
    );

    const [artikelDetailKalkulationen, setArtikelDetailKalkulationen] = React.useState<
        ArtikelDetailKalkulation[] | null
    >(null);

    const [
        lieferantenrechnungAraPosition,
        setLieferantenrechnungAraPosition,
    ] = React.useState<LieferantenrechnungAraPosition | null>(null);

    React.useEffect(() => {
        if (
            !lieferantenrechnungAraPositionList.isLoading &&
            !lieferantenrechnungAraPositionList.isFetching &&
            lieferantenrechnungAraPosition === null
        ) {
            if (lieferantenrechnungAraPositionList.data?.length === 1) {
                setLieferantenrechnungAraPosition(cloneDeep(lieferantenrechnungAraPositionList.data[0]));
                setSavingState(SavingState.notChanged);

                if (
                    lieferantenrechnungAraPositionList.data[0].artikelGruppeNr &&
                    lieferantenrechnungAraPositionList.data[0].artikelNr
                ) {
                    getArtikelDetailKalkulationen({
                        artikelGruppeNr: lieferantenrechnungAraPositionList.data[0].artikelGruppeNr,
                        artikelNr: lieferantenrechnungAraPositionList.data[0].artikelNr,
                    }).then((newArtikelDetailKalkulationen) =>
                        setArtikelDetailKalkulationen(newArtikelDetailKalkulationen),
                    );
                }
            } else {
                setLieferantenrechnungAraPosition({
                    ...defaultLieferantenrechnungAraPosition,
                    rNr,
                    pos,
                    posMwst: kunde.land === 'AT' ? 20 : 0,
                });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [lieferantenrechnungAraPosition, lieferantenrechnungAraPositionList, rNr, pos, auth, kunde]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !lieferantenrechnungAraPositionList.isLoading &&
            !lieferantenrechnungAraPositionList.isFetching &&
            lieferantenrechnungAraPositionList.data?.length === 1 &&
            lieferantenrechnungAraPosition !== null &&
            !isEqual(lieferantenrechnungAraPositionList.data[0], lieferantenrechnungAraPosition)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference(
                'lieferantenrechnungAraPosition',
                lieferantenrechnungAraPosition,
                lieferantenrechnungAraPositionList.data[0],
            );
        }
    }, [savingState, lieferantenrechnungAraPositionList, lieferantenrechnungAraPosition]);

    React.useEffect(() => {
        if (lieferantenrechnungAraPosition) {
            if (lieferantenrechnungAraPosition.posMenge && lieferantenrechnungAraPosition.posEPreis) {
                const netto =
                    Math.round(
                        lieferantenrechnungAraPosition.posMenge * lieferantenrechnungAraPosition.posEPreis * 100,
                    ) / 100;
                const mwstFactor = lieferantenrechnungAraPosition.posMwst
                    ? 1 + lieferantenrechnungAraPosition?.posMwst / 100
                    : 1;
                const brutto = lieferantenrechnungAraPosition.posMwst
                    ? Math.round(netto * mwstFactor * 100) / 100
                    : netto;

                setLieferantenrechnungAraPosition({
                    ...lieferantenrechnungAraPosition,
                    posNetto: netto,
                    posBrutto: brutto,
                });
            } else {
                setLieferantenrechnungAraPosition({
                    ...lieferantenrechnungAraPosition,
                    posNetto: 0,
                    posBrutto: 0,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        lieferantenrechnungAraPosition?.posMenge,
        lieferantenrechnungAraPosition?.posEPreis,
        lieferantenrechnungAraPosition?.posMwst,
    ]);

    const validate = (): boolean => {
        if (!lieferantenrechnungAraPosition?.posText?.trim()) {
            setValidationError('Bitte geben Sie einen Text ein.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }
        return true;
    };

    const save = () => {
        if (lieferantenrechnungAraPosition && savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                if (lieferantenrechnungAraPosition.pos === -1) {
                    addLieferantenrechnungAraPosition(lieferantenrechnungAraPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateLieferantenrechnungAraPosition(lieferantenrechnungAraPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    };

    const renderTextField = (
        column: keyof LieferantenrechnungAraPosition,
        label: string,
        options: FormTextFieldOptions = {},
    ) => (
        <FormTextField
            value={(lieferantenrechnungAraPosition?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferantenrechnungAraPosition) {
                    setLieferantenrechnungAraPosition({
                        ...lieferantenrechnungAraPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof LieferantenrechnungAraPosition,
        label: string,
        options: FormDecimalFieldOptions = {},
    ) => (
        <FormDecimalField
            value={(lieferantenrechnungAraPosition?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferantenrechnungAraPosition) {
                    setLieferantenrechnungAraPosition({
                        ...lieferantenrechnungAraPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderArtikelDetailSelect = () => (
        <ArtikelDetailSelect
            artikelGruppeNr={lieferantenrechnungAraPosition?.artikelGruppeNr}
            artikelNr={lieferantenrechnungAraPosition?.artikelNr}
            disabled
            onChange={() => {}}
        />
    );

    const renderEinheit = (
        column: keyof LieferantenrechnungAraPosition,
        ekPreisColumn: keyof LieferantenrechnungAraPosition,
        ePreisColumn: keyof LieferantenrechnungAraPosition,
        label: string,
    ) => (
        <EinheitByCode
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            value={(lieferantenrechnungAraPosition?.[column] as string) || ''}
            menuPlacement="top"
            onChange={(newEinheit) => {
                let ekPreis = 0;
                let ePreis = 0;
                if (artikelDetailKalkulationen && newEinheit) {
                    const kalkulation = artikelDetailKalkulationen.find((k) => k.eh === newEinheit);
                    ekPreis = kalkulation?.ek1 || 0;
                    ePreis = kalkulation?.vk || 0;
                }

                if (lieferantenrechnungAraPosition) {
                    setLieferantenrechnungAraPosition({
                        ...lieferantenrechnungAraPosition,
                        [column]: newEinheit,
                        [ekPreisColumn]: ekPreis,
                        [ePreisColumn]: ePreis,
                    });
                }
            }}
        />
    );

    return (
        <Dialog
            hidden={false}
            title="Lieferantenrechnungsposition"
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={1100}
            onDismiss={() => {
                onClose(false);
            }}
        >
            <form className={formStyles.form}>
                <div className={styles.artikelLine}>
                    <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                    <div>{renderArtikelDetailSelect()}</div>
                </div>
                <div className={formStyles.line}>
                    <div>{renderTextField('posText', 'Text', { maxLength: 100, disabled: true })}</div>
                </div>

                <div className={formStyles.spacerHeight100} />

                <div className={styles.posLine}>
                    <div>{renderEinheit('posEinh', 'posEkPreis', 'posEPreis', 'Einheit')}</div>
                    <div>{renderDecimalField('posMenge', 'Menge', { fixed: 3 })}</div>
                    {/* <div>{renderDecimalField('posEkPreis', 'EK-Preis', { fixed: 3 })}</div> */}
                    <div>{renderDecimalField('posEPreis', 'EK-Preis', { fixed: 3 })}</div>
                    <div>{renderDecimalField('posNetto', 'EK-Netto', { disabled: true })}</div>
                    <div>{renderDecimalField('posMwst', 'MwSt.', { fixed: 0 })}</div>
                    <div>{renderDecimalField('posBrutto', 'EK-Brutto', { disabled: true })}</div>
                </div>

                <Timestamps
                    modifiedDate={lieferantenrechnungAraPosition?.updDat}
                    modifiedDecimalTime={lieferantenrechnungAraPosition?.updUhr}
                    modifiedUser={lieferantenrechnungAraPosition?.updUser}
                    showCreated={false}
                />

                {validationError && <div className={formStyles.error}>{validationError}</div>}
                {savingState === SavingState.error && <div className={formStyles.error}>Fehler beim Speichern.</div>}

                <DialogFooter>
                    {savingState === SavingState.saving ? (
                        <PrimaryButton text="Speichere..." disabled />
                    ) : (
                        <PrimaryButton
                            onClick={save}
                            text="Speichern"
                            disabled={
                                savingState === SavingState.notChanged || lieferantenrechnungAraPositionList.isLoading
                            }
                        />
                    )}
                </DialogFooter>
            </form>
            {(lieferantenrechnungAraPositionList.isLoading ||
                lieferantenrechnungAraPositionList.isFetching ||
                !lieferantenrechnungAraPosition) && <LoadingIndicator />}
        </Dialog>
    );
};
